import { useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { AccountDetailModal } from './AccountDetailModal.js'
import { formatDate as formatDetailDate, Skeleton } from '../../core/index.js'
import type { AdminClientSummary } from '../../clients/index.js'
import { OperatorIcon } from '../../core/index.js'
import { SortableTh, useSort, type SortAccessors } from '../../core/index.js'
import { NewOperatorModal } from './NewOperatorModal.js'
import { EmptyState, NewButton } from './formParts.js'
import type { ComponentProps } from 'react'
import type { AccountsAdminApi } from '../adminApi.js'
import type { ClientsAdminApi } from '../../clients/index.js'
import { LoadFailure, RefreshButton } from '../../core/index.js'

/** The admin calls this screen makes itself, and what the screens it opens need. */
type AdminApi = AccountsAdminApi & ClientsAdminApi & ComponentProps<typeof NewOperatorModal>['api']


interface OperatorRow {
  key: string
  name: string | null
  email: string
  client: AdminClientSummary
  /** Null for the legacy dashboard login, which has no creation date of its own. */
  createdAt: string | null
  isLegacy: boolean
}

function formatDate(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

const OPERATOR_SORT: SortAccessors<OperatorRow, 'name' | 'email' | 'client' | 'environment' | 'created'> = {
  name: (row) => row.name ?? row.email,
  email: (row) => row.email,
  client: (row) => row.client.organizationName,
  environment: (row) => row.client.environment,
  created: (row) => row.createdAt,
}

/**
 * Every operator login across every client, one row each, with the client
 * it belongs to. There is no single "all operators" endpoint, so this asks
 * for each active client's operators in parallel - sharing the cache the
 * client panel uses, so a change made there shows up here. A client's
 * legacy dashboard login is listed too, marked as such, because it is
 * equally a way into that client's dashboard.
 */
export function OperatorList({ api }: { api: AdminApi }) {
  const [viewing, setViewing] = useState<OperatorRow | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const clientsQuery = useQuery({ queryKey: ['admin', 'clients'], queryFn: () => api.listClients() })
  const activeClients = (clientsQuery.data ?? []).filter((client) => client.status === 'active')

  const operatorQueries = useQueries({
    queries: activeClients.map((client) => ({
      queryKey: ['admin', 'operators', client.registrationId],
      queryFn: () => api.listOperators(client.registrationId),
    })),
  })

  const isLoading = clientsQuery.isLoading || operatorQueries.some((query) => query.isLoading)
  const isRefreshing = clientsQuery.isFetching || operatorQueries.some((query) => query.isFetching)
  const refresh = () => Promise.all([clientsQuery.refetch(), ...operatorQueries.map((query) => query.refetch())])
  const failedClients = activeClients.filter((_, index) => operatorQueries[index]?.isError)

  const rows: OperatorRow[] = activeClients
    .flatMap((client, index): OperatorRow[] => [
      ...(operatorQueries[index]?.data ?? []).map((operator) => ({
        key: operator.operatorId,
        name: `${operator.firstName} ${operator.lastName}${operator.invitationStatus === 'pending' ? ' · invited, waiting' : ''}`,
        email: operator.email,
        client,
        createdAt: operator.createdAt,
        isLegacy: false,
      })),
      ...(client.dashboardUsername
        ? [{ key: `legacy-${client.registrationId}`, name: null, email: client.dashboardUsername, client, createdAt: null, isLegacy: true }]
        : []),
    ])
    .sort(
      (a, b) =>
        a.client.organizationName.localeCompare(b.client.organizationName) ||
        (a.name ?? a.email).localeCompare(b.name ?? b.email) ||
        a.key.localeCompare(b.key),
    )

  const { sorted, header } = useSort(rows, OPERATOR_SORT)

  const clientsWithoutLogin = activeClients.filter(
    (client, index) => operatorQueries[index]?.isSuccess && operatorQueries[index]?.data?.length === 0 && !client.dashboardUsername,
  )

  return (
    <div className="max-w-5xl mx-auto px-7 py-6">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="text-sm text-ink-mid">
          {!isLoading && !clientsQuery.isError && `${rows.length} ${rows.length === 1 ? 'operator' : 'operators'} across ${activeClients.length} active ${activeClients.length === 1 ? 'client' : 'clients'}`}
        </div>
        <div className="flex items-center gap-2">
          <RefreshButton onRefresh={refresh} refreshing={isRefreshing} />
          <NewButton label="New operator" onClick={() => setIsCreating(true)} />
        </div>
      </div>

      {failedClients.length > 0 && (
        <div className="border border-coral bg-coral-lt px-4 py-3 mb-4 text-xs text-ink">
          Couldn't load operators for {failedClients.map((client) => client.organizationName).join(', ')}, so this list is incomplete.
        </div>
      )}
      {clientsWithoutLogin.length > 0 && (
        <div className="border border-gold bg-gold-lt px-4 py-3 mb-4 text-xs text-ink">
          No operator login yet: {clientsWithoutLogin.map((client) => client.organizationName).join(', ')}. Create one with New operator, or from the
          client's panel under All Clients.
        </div>
      )}

      {isLoading ? (
        <Skeleton lines={5} label="Loading operators" />
      ) : clientsQuery.isError ? (
        <LoadFailure message="Failed to load clients. Confirm the API is reachable." onRetry={() => void clientsQuery.refetch()} retrying={clientsQuery.isFetching} />
      ) : rows.length === 0 ? (
        <EmptyState message="No operators yet. One is created when a client is onboarded; add more here." actionLabel="Create an operator" onAction={() => setIsCreating(true)} />
      ) : (
        <div className="bg-white border border-marine/15 overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-marine/15 text-[10px] uppercase tracking-wide text-ink-mid">
                <SortableTh className="px-4 py-2" label="Operator" {...header('name')} />
                <SortableTh className="px-4 py-2" label="Login email" {...header('email')} />
                <SortableTh className="px-4 py-2" label="Client" {...header('client')} />
                <SortableTh className="px-4 py-2" label="Environment" {...header('environment')} />
                <SortableTh className="px-4 py-2" label="Created" {...header('created')} />
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => (
                <tr key={row.key} className="border-b border-marine/10">
                  <td className="px-4 py-3 font-bold text-ink">
                    <span className="flex items-center gap-2">
                    <OperatorIcon className="w-4 h-4 text-marine" />
                    {/* The legacy login's name isn't part of the client summary, so the tag stands in for it. */}
                    <button type="button" onClick={() => setViewing(row)} className="font-bold text-ink text-left underline decoration-marine/30 underline-offset-2 hover:text-marine hover:decoration-marine">
                      {row.isLegacy ? <span className="px-2 py-0.5 text-[10px] font-bold bg-silver-lt text-silver no-underline">Legacy login</span> : row.name}
                    </button>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-ink-mid">{row.email}</td>
                  <td className="px-4 py-3 text-ink">{row.client.organizationName}</td>
                  <td className="px-4 py-3 text-ink-mid capitalize">{row.client.environment}</td>
                  <td className="px-4 py-3 text-ink-mid whitespace-nowrap">{row.createdAt ? formatDate(row.createdAt) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {isCreating && <NewOperatorModal api={api} onClose={() => setIsCreating(false)} />}
      {viewing && (
        <AccountDetailModal
          kind={viewing.isLegacy ? 'Legacy dashboard login' : 'Operator'}
          name={viewing.name ?? viewing.email}
          client={viewing.client}
          onClose={() => setViewing(null)}
          fields={[
            { label: 'Login email', value: viewing.email },
            { label: 'Kind', value: viewing.isLegacy ? 'The single shared login stored on the client itself' : 'Named operator account' },
            { label: 'Access', value: 'Every facility of this client' },
            { label: 'Created', value: viewing.createdAt ? formatDetailDate(viewing.createdAt) : 'Not recorded' },
          ]}
        />
      )}
    </div>
  )
}
